import { useCallback } from "react";
import type { Client } from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import { removeClient, saveClient } from "@/store/api";

// Acciones de clientes: alta (incluye crear desde una venta), edición y borrado.
export const useClientActions = (ctx: StoreCtx) => {
  const { setData, run, setError } = ctx;

  const addClient = useCallback(
    (c: Client): Promise<Client | null> =>
      (async () => {
        try {
          // El id se omite a propósito: una cuenta nueva siempre se inserta.
          const saved = await saveClient({ ...c, id: "" });
          setData((p) => ({ ...p, clients: [...p.clients, saved] }));
          setError(null);
          return saved;
        } catch (e) {
          setError(e instanceof Error ? e.message : "No se pudo guardar el cliente.");
          return null;
        }
      })(),
    [setData, setError]
  );

  const updateClient = useCallback(
    (c: Client) =>
      run(async () => {
        const saved = await saveClient(c);
        setData((p) => ({
          ...p,
          clients: p.clients.map((x) => (x.id === saved.id ? saved : x)),
        }));
      }),
    [run, setData]
  );

  const deleteClient = useCallback(
    (id: string) =>
      run(async () => {
        await removeClient(id);
        setData((p) => ({ ...p, clients: p.clients.filter((x) => x.id !== id) }));
      }),
    [run, setData]
  );

  // Borra varios clientes de una sola tanda.
  const deleteClients = useCallback(
    (ids: string[]) =>
      run(async () => {
        const unique = Array.from(new Set(ids.filter(Boolean)));
        if (unique.length === 0) return;
        await Promise.all(unique.map((id) => removeClient(id)));
        const set = new Set(unique);
        setData((p) => ({ ...p, clients: p.clients.filter((x) => !set.has(x.id)) }));
      }),
    [run, setData]
  );

  return { addClient, updateClient, deleteClient, deleteClients };
};